/**
 * The declared-VAT refusal contract, shared by the api (which turns the
 * database's refusal into a coded 400 body — once, in `app.onError`, plus the
 * one clearing route that returns envelopes without throwing) and the web
 * (which reads `code === VAT_DECLARED` off the envelope and names the period
 * in its toast).
 *
 * Where the string is produced: the `BEFORE DELETE OR UPDATE OF <amount-ish
 * columns>` trigger `guard_declared_vat_entry` on `ledger_entries` refuses to
 * touch a row whose `vat_settlement_id` points at a filed (un-voided) return.
 * It raises with `ERRCODE = 'RV001'` and the message
 *
 *     VAT_DECLARED|<settlement uuid>|<period_start>|<period_end>|<human text>
 *
 * where the two dates are `YYYY-MM-DD`, or BOTH empty when RLS hid the
 * settlement row from the caller (`VAT_DECLARED|<uuid>|||<text>`). The human
 * text is everything after the fourth `|` and is never split further.
 *
 * Why "anywhere in the string", not position 0: the writer RPCs that catch-all
 * with `SQLERRM` pass the message through verbatim, but `undo_import_split`
 * wraps it (`'Cannot undo: ' || SQLERRM`), and PostgREST may prefix its own
 * context. Anything before the marker is discarded.
 */
export const VAT_DECLARED = "VAT_DECLARED";
const MARKER = `${VAT_DECLARED}|`;
/**
 * Parse a database error message for the guard's refusal. Returns `null` when
 * the marker is absent OR the fields after it do not fit the contract (fewer
 * than four `|`-delimited fields, or an empty settlement id) — callers then
 * fall back to their generic error handling with the message untouched.
 */
export function parseVatDeclared(message) {
    if (!message)
        return null;
    const at = message.indexOf(MARKER);
    if (at < 0)
        return null;
    // Split ONLY the three leading fields; the remainder is the human text and
    // may legitimately contain `|`, so it is taken whole rather than re-joined.
    const rest = message.slice(at + MARKER.length);
    const fields = [];
    let cursor = 0;
    for (let i = 0; i < 3; i += 1) {
        const sep = rest.indexOf("|", cursor);
        if (sep < 0)
            return null;
        fields.push(rest.slice(cursor, sep).trim());
        cursor = sep + 1;
    }
    const [settlementId, periodStart, periodEnd] = fields;
    const text = rest.slice(cursor).trim();
    if (!settlementId || !text)
        return null;
    return {
        code: VAT_DECLARED,
        error: text,
        vat_settlement_id: settlementId,
        period_start: periodStart || null,
        period_end: periodEnd || null,
    };
}
//# sourceMappingURL=vatDeclared.js.map